import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'react-toastify';

import Label from '../../../components/shared/Label';
import {
  ActiveSlide,
  useSlidesStore,
} from '../../../context/zustand/slidesStore';
import { useAuth } from '../../../hooks/useAuth';
import { usePorteeSlideManager, usePotree } from '../../../hooks/usePotree';
import { useToggle } from '../../../hooks/useToggle';
import { OrtoScene } from '../../../types';
import { OrtoSceneHelper } from '../../../utils/OrtoSceneHelper';
import {
  authTokenHeader,
  cn,
  contentfulRichText,
  unescapeHTML,
} from '../../../utils/helper';
import Authenticated from '../shared/Authenticated';
import { EditedSlide, editedSlideHasErrors } from './SlideEditor';
import SlideMenu from './SlideMenu';


type SlideProps = {
  slide: OrtoScene;
  order: string;
  isParent?: boolean;
};

const Slide: React.FC<SlideProps> = ({ slide, order, isParent }) => {
  const { token } = useAuth();
  const { project, setProject, potreeViewer } = usePotree();
  const { potreeDisplayMode } = useToggle();

  const potreeSlideManager = usePorteeSlideManager();

  const state = useSlidesStore();
  const { activeSlide, setActiveSlide } = state;

  const _showDescription = (
    scene: OrtoScene,
    activeSlide: ActiveSlide
  ): boolean => {
    // Only show the description on the active (i.e. currently selected) slide
    const isActive = scene.id === activeSlide.id;
    // Only show the description if it is defined and not all whitespace
    // Don't show blank rich text descriptions (generated by Quill)
    const description = scene.field_description?.value;
    return isActive && contentfulRichText(description);
  };

  const updateSlide = async (slide: EditedSlide) => {
    const body: any = JSON.stringify(
      OrtoSceneHelper.getScene(
        slide.field_scene_type,
        potreeViewer,
        slide.label,
        slide.field_description,
        slide.json_data[0]?.value?.view?.transitionDuration
      )
    );

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/v2/scene/${slide.id}`,
        {
          method: 'POST',
          headers: authTokenHeader(token),
          body,
        }
      );

      const updatedScene = await response.json();

      if (!updatedScene || !response.ok) {
        toast.error(`Updating scene '${updatedScene}' failed.`);
        return;
      }

      toast.success(`Scene '${slide.label}' updated.`);

      setProject((prevProject: any) => ({
        ...prevProject,
        field_scenes: {
          ...prevProject.field_scenes,
          [potreeDisplayMode]: {
            ...prevProject.field_scenes[potreeDisplayMode],
            scenes: prevProject.field_scenes[potreeDisplayMode].scenes.map(
              (scene: OrtoScene) =>
                scene.id === slide.id ? updatedScene : scene
            ),
          },
        },
      }));
    } catch (error: any) {
      console.log('API request failed:', error.message);
      throw Error(error.message);
    }
  };

  const deleteSlide = async (slideId: string) => {
    const slides = project?.field_scenes[potreeDisplayMode]?.scenes;
    if (!slides) {
      toast.error(`Could not find the ${potreeDisplayMode} slides`);
      return;
    }

    const deletedSlide = slides?.find((slide) => slide.id === slideId);
    if (!deletedSlide) {
      toast.error('Could not find the slide to delete');
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/v2/scene/${slideId}`,
        {
          method: 'DELETE',
          headers: authTokenHeader(token),
        }
      );

      const deletedScene = await response.json();

      if (!deletedScene || !response.ok) {
        toast.error(`Deleting scene '${deletedSlide.label}' failed.`);
        return;
      }

      toast.success(`Scene '${deletedSlide.label}' deleted.`);

      setProject((prevProject: any) => ({
        ...prevProject,
        field_scenes: {
          ...prevProject.field_scenes,
          [potreeDisplayMode]: {
            ...prevProject.field_scenes[potreeDisplayMode],
            scenes:
              prevProject.field_scenes[potreeDisplayMode]?.scenes.filter(
                (scene: OrtoScene) => scene.id !== slideId
              ) || [],
          },
        },
      }));

      // If the deleted slide was currently active, go to the first slide
      if (activeSlide.id === slideId) {
        potreeSlideManager.goToSlide(slides[0], potreeDisplayMode);
      } else {
        // NOTE: hack for updating the UI, otherwise the active slide is not highlighted
        setActiveSlide(activeSlide);
      }
    } catch (error: any) {
      console.log('API request failed:', error.message);
      throw Error(error.message);
    }
  };

  const handleUpdateSlide = (slide: EditedSlide) => {
    if (editedSlideHasErrors(slide)) {
      return;
    }

    updateSlide(slide);
  };

  const { id, label } = slide;

  const getInactiveSlideStyle = () => {
    if (isParent) {
      return 'border border-[#DEDEDE] bg-[#F1F1F1] text-primary';
    }
    return 'border border-muted bg-white text-primary';
  };

  // TODO: this is another hack to make the presentation accordion work. The slide does NOT need this -> should be removed eventually
  const [isOpen, setIsOpen] = useState<boolean>(false);
/*
const [{ isDragging }, dragRef] = useDrag({
  item: { type: "item"},
  begin: () => {
    console.log("drag began");
  },
  end: (dropResult) => {
    console.log("drag end");
  },
  collect: (monitor) => ({
    isDragging: monitor.isDragging(),
  }),
});
*/
  return (
    <div
      key={`slide-${id}`}
      className={cn(
        'flex w-full flex-col gap-4 rounded-lg py-3',
        activeSlide.id === id
          ? 'bg-primary text-white'
          : getInactiveSlideStyle()
      )}
    >
      {/* Scene card label and menu */}
      <div draggable="true"
        className="flex cursor-pointer items-center justify-between"
        onClick={() => {
          potreeSlideManager.goToSlide(slide, potreeDisplayMode);
          setIsOpen((prev) => !prev);
        }}
        onDrag={(event) => {
        	//this.dragTask(event)
        	console.log('drag');
        }}
      >
        <div className="flex items-center pl-4">
          <div className="flex items-baseline gap-2">
            <span className="text-xs-regular">{order}</span>
            <Label text={label} classNames="text-sm-regular text-bold" />
          </div>
        </div>

        <div className="flex items-center justify-end">
          <Authenticated>
            {/* NOTE adding an accordion chevron here, because the original shadcn chevron is hidden (display: none) */}
            {/* TODO this condition should not be necessary once slides and presentations are cleanly separated from one another */}
            {isParent && (
              <ChevronDown
                className={cn(
                  '-mr-3 h-4 shrink-0 text-accent transition-all duration-200 hover:bg-inherit hover:text-secondary',
                  isOpen ? 'rotate-180' : ''
                )}
              />
            )}

            <SlideMenu
              key={`slide_menu_${id}`}
              slideNumber={order}
              initialSlideState={slide}
              handleUpdate={handleUpdateSlide}
              handleDelete={() => {
                deleteSlide(id);
              }}
            />
          </Authenticated>
        </div>
      </div>

      {/* Scene card description */}
      {_showDescription(slide, activeSlide) && (
        <div
          className="flex flex-col gap-4 px-4 font-sans leading-snug text-muted [&_a]:text-white [&_a]:underline [&_p]:text-sm-regular [&_strong]:text-white"
          dangerouslySetInnerHTML={{
            __html: unescapeHTML(slide.field_description!.value),
          }}
        />
      )}
    </div>
  );
};

export default Slide;
